import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { categoryMap } from "../data/categoryMap";


const statementTextById = (id: string, language: "en" | "vn"): string => {
  for (const category of categoryMap) {
    const match = category.statements.find((s) => s.id === id);
    if (match) return match.text[language];
  }
  return `Statement ${id}`;
};
const renderAIText = (text: string) => {
  return text.split("\n").map((line, lineIndex) => {
    const parts = line.split(/(\*\*.*?\*\*)/g);

    return (
      <div
        key={lineIndex}
        style={{
          marginBottom: line.trim() === "" ? "0.6rem" : "0.35rem",
        }}
      >
        {parts.map((part, partIndex) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <strong key={partIndex}>
                {part.slice(2, -2)}
              </strong>
            );
          }

          return <span key={partIndex}>{part}</span>;
        })}
      </div>
    );
  });
};

import { db } from "../services/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

interface Response {
  isManager: boolean;
  responses: {
    statementId: string;
    score: number;
  }[];
}


interface CategoryAverages {
  category: string;
  Manager: number;
  Team: number;
}

interface TeamScanAIReport {
  interpretationEn: string;
  interpretationVn: string;
  nextStepsEn: string;
  nextStepsVn: string;
}

export default function Results() {
  const { code } = useParams();
  const [language, setLanguage] = useState<"en" | "vn">("vn");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<CategoryAverages[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
const [statementDetails, setStatementDetails] = useState<
  { statement: string; managerAvg: number; teamAvg: number }[]
>([]);
  const [mostVariableStatement, setMostVariableStatement] = useState<string | null>(null);
  const [aiReport, setAiReport] =
  useState<TeamScanAIReport | null>(null);

const [aiReportLoading, setAiReportLoading] =
  useState(false);

const [aiReportError, setAiReportError] =
  useState<string | null>(null);

const [aiRefreshKey, setAiRefreshKey] = useState(0);


useEffect(() => {
  const fetchData = async () => {
    try {
      const q = query(collection(db, "responses"), where("code", "==", code));
      const snapshot = await getDocs(q);
      const results: Response[] = [];

      snapshot.forEach((doc) => {
        results.push(doc.data() as Response);
      });

  const managers = results.filter((r) => r.isManager);
const team = results.filter((r) => !r.isManager);

if (managers.length === 0 && team.length < 3) {
  setError("Not enough responses: missing manager input and fewer than 3 team members.");
} else if (managers.length === 0) {
  setError("Waiting for the manager to submit their input.");
} else if (team.length < 3) {
  setError(`Only ${team.length} team response${team.length === 1 ? "" : "s"} so far — need at least 3.`);
} else {
 const allCategoryIds = new Set<string>();

managers.forEach((manager) => {
  manager.responses.forEach((r) => allCategoryIds.add(r.statementId.split(".")[0]));
});
team.forEach((member) => {
  member.responses.forEach((r) => allCategoryIds.add(r.statementId.split(".")[0]));
});

const uniqueCategoryIds = Array.from(allCategoryIds);


  const categoryStats = uniqueCategoryIds.map((categoryId) => {
  const matchedCategory = categoryMap.find((c) => c.id === categoryId);
  const label = matchedCategory?.name[language] || categoryId;

 const managerScores = managers
  .flatMap((m) => m.responses)
  .filter((r) => r.statementId.startsWith(categoryId + "."))
  .map((r) => r.score);


  const teamScores = team.flatMap((t) =>
    t.responses
      .filter((r) => r.statementId.startsWith(categoryId + "."))
      .map((r) => r.score)
  );

  const avg = (arr: number[]) =>
    arr.length > 0 ? arr.reduce((sum, n) => sum + n, 0) / arr.length : 0;

  const stdDev = (arr: number[]) => {
    const mean = avg(arr);
    return arr.length > 0
      ? Math.sqrt(arr.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / arr.length)
      : 0;
  };

  const managerAvg = managerScores.length > 0
  ? avg(managerScores)
  : null; // allow null if skipped

  const teamAvg = avg(teamScores);

  return {
    categoryId, // used for internal matching
    category: label, // translated display name
    Manager: Math.round(managerAvg * 10) / 10,
    Team: Math.round(teamAvg * 10) / 10,
    overallAvg: (managerAvg + teamAvg) / 2,
    stdDev: stdDev(teamScores),
    gap: Math.abs(managerAvg - teamAvg),
    highlights: [] as string[],
  };
});

        // Highlight top 2 per rule
        const addHighlight = (key: "overallAvg" | "stdDev" | "gap", label: string, sortAsc = true) => {
          const sorted = [...categoryStats].sort((a, b) =>
            sortAsc ? a[key] - b[key] : b[key] - a[key]
          );
          sorted.slice(0, 2).forEach((item) => {
            const cat = categoryStats.find((c) => c.category === item.category);
            if (cat && !cat.highlights.includes(label)) {
              cat.highlights.push(label);
            }
          });
        };

        addHighlight("overallAvg", "lowestAvg", true);
        addHighlight("stdDev", "highestStdDev", false);
        addHighlight("gap", "largestGap", false);
console.log("📊 Final chart data", categoryStats);

        setChartData(categoryStats);
      }
    } catch (err) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [code, language]);

  useEffect(() => {
  if (
  !code ||
  loading ||
  error ||
  chartData.length === 0
) {
  return;
}

  const controller = new AbortController();

  const fetchAIReport = async () => {
    setAiReportLoading(true);
    setAiReportError(null);

    try {
      const response = await fetch(
        "/api/generateTeamScanAnalysis",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code }),
          signal: controller.signal,
        }
      );

      const responseText = await response.text();

      let data: {
        success?: boolean;
        report?: TeamScanAIReport;
        error?: string;
      };

      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error(
          "The AI report service returned an invalid response."
        );
      }

      if (!response.ok || !data.success || !data.report) {
        throw new Error(
          data.error || "The AI report could not be generated."
        );
      }

      setAiReport(data.report);
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.name === "AbortError"
      ) {
        return;
      }

      console.error("AI report loading failed:", error);

      setAiReportError(
        error instanceof Error
          ? error.message
          : "The AI report could not be generated."
      );
    } finally {
      if (!controller.signal.aborted) {
        setAiReportLoading(false);
      }
    }
  };

  fetchAIReport();

  return () => {
    controller.abort();
  };
}, [code, loading, error, chartData.length, aiRefreshKey]);
  
useEffect(() => {
  if (!selectedCategoryId || chartData.length === 0) return;

  const fetchStatementDetails = async () => {
    console.log("Fetching statements for:", selectedCategoryId); // ✅ This logs to browser

    const q = query(collection(db, "responses"), where("code", "==", code));
    const snapshot = await getDocs(q);
    const results: Response[] = [];
    snapshot.forEach((doc) => results.push(doc.data() as Response));

    const statementMap: {
      [statement: string]: { managerScores: number[]; teamScores: number[] };
    } = {};

 for (const r of results) {
  for (const s of r.responses as { statementId: string; score: number }[]) {
    const [catId] = s.statementId.split(".");
    const matchedCategory = categoryMap.find((c) => c.id === catId);
   if (matchedCategory?.id !== selectedCategoryId) continue;

    if (!statementMap[s.statementId]) {
      statementMap[s.statementId] = { managerScores: [], teamScores: [] };
    }
    if (r.isManager) {
      statementMap[s.statementId].managerScores.push(s.score);
    } else {
      statementMap[s.statementId].teamScores.push(s.score);
    }
  }
}


 const averages = Object.entries(statementMap)
  .filter(([id]) => {
    const [catId] = id.split(".");
    return catId === selectedCategoryId;
  })
  .map(([statementId, { managerScores, teamScores }]) => {
    const avg = (arr: number[]) =>
      arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    const stdDev = (arr: number[]) => {
      const mean = avg(arr);
      return arr.length > 0
        ? Math.sqrt(arr.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / arr.length)
        : 0;
    };

    const statementText = statementTextById(statementId, language);

    return {
      statement: statementText,
      managerAvg: Math.round(avg(managerScores) * 10) / 10,
      teamAvg: Math.round(avg(teamScores) * 10) / 10,
      variability: stdDev([...managerScores, ...teamScores])
    };
  });

    setStatementDetails(averages);
    const mostVariable = averages.reduce((a, b) =>
  a.variability > b.variability ? a : b
);
setMostVariableStatement(mostVariable.statement);

  };

  fetchStatementDetails();
}, [selectedCategoryId, chartData, code]);

  if (loading) return <p>Loading...</p>;
if (error) {
  return (
    <div style={{ padding: "2rem", color: "crimson", fontWeight: "bold" }}>
      ❌ {error}
    </div>
  );
}


  return (
    <div style={{ padding: "1rem", position: "relative" }}>
    <div style={{ position: "absolute", top: "1rem", right: "1rem" }}>
      <img
        src="/logo-nononsense.jpg"
        alt="No-Nonsense Logo"
        style={{ width: "200px", height: "auto" }}
      />
       </div>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>
  Results for Code: {code}
</h2>
    {selectedCategoryId && (
  <p style={{ color: "gray" }}>
    Selected: {categoryMap.find((c) => c.id === selectedCategoryId)?.name[language] || "(none)"}
  </p>
)}
<div style={{ marginBottom: "1rem" }}>
  <button
    onClick={() => setLanguage(language === "en" ? "vn" : "en")}
    title={language === "en" ? "Chuyển sang Tiếng Việt" : "Switch to English"}
    style={{
      padding: "0.3rem 0.8rem",
      fontSize: "0.9rem",
      backgroundColor: "#eee",
      border: "1px solid #ccc",
      borderRadius: "4px",
      cursor: "pointer"
    }}
  >
    Language: {language === "en" ? "Tiếng Việt" : "English"}
  </button>
</div>

<div style={{ display: "flex", width: "100%", minHeight: 650 }}>
  {/* RULE LEGEND on LEFT */}
  <div style={{ width: "180px", paddingRight: "1rem", fontSize: "12px" }}>
  <div><span style={{ color: "#e74c3c" }}>🔴 {language === "en" ? "Low average" : "Điểm trung bình thấp"}</span></div>
  <div><span style={{ color: "#f39c12" }}>🟠 {language === "en" ? "High variability" : "Độ lệch cao"}</span></div>
  <div><span style={{ color: "#3498db" }}>🔵 {language === "en" ? "Large gap" : "Khoảng cách lớn"}</span></div>
  <div><span style={{ color: "#8e44ad" }}>🟣 {language === "en" ? "Multiple highlights" : "Nhiều điểm nổi bật"}</span></div>
</div>


  {/* CHART & FLOATING POPUP */}
  <div style={{ flex: 1, position: "relative" }}>
    {chartData.length === 0 ? (
      <p style={{ color: "gray" }}>No chart data available.</p>
    ) : (
      <ResponsiveContainer>
        <RadarChart data={chartData} key={language}>
          <PolarGrid />   
            <PolarAngleAxis
            dataKey="category"
            tickLine={false}
            type="category"
            tick={({ payload, x, y, textAnchor }) => {
             const categoryId = chartData.find((c) => c.category === payload?.value)?.categoryId;
const translatedLabel = payload?.value;
const highlights = chartData.find((c) => c.categoryId === categoryId)?.highlights || [];


              const combinedColor =
                highlights.length >= 2
                  ? "#8e44ad"
                  : highlights.includes("lowestAvg")
                  ? "#e74c3c"
                  : highlights.includes("highestStdDev")
                  ? "#f39c12"
                  : highlights.includes("largestGap")
                  ? "#3498db"
                  : null;

              return (
                <g style={{ pointerEvents: "all" }}>
                  {combinedColor && (
                    <circle
                      cx={x}
                      cy={y - 4}
                      r={24}
                      fill={combinedColor}
                      opacity={0.3}
                      className="pulsating-circle"
                      style={{ pointerEvents: "none" }}
                    />
                  )}
                  <text
                    x={x}
                    y={y}
                    textAnchor={textAnchor}
                    fill="#333"
                    fontWeight={highlights.length ? "bold" : "normal"}
                    fontSize="14"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
    if (categoryId) setSelectedCategoryId(categoryId);
  }}
>
  {translatedLabel}
</text>
                </g>
              );
            }}
          />
          <PolarRadiusAxis angle={30} domain={[0, 4]} />
          <Legend />
          <Radar
            name="Manager"
            dataKey="Manager"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.6}
          />
          <Radar
            name="Team"
            dataKey="Team"
            stroke="#82ca9d"
            fill="#82ca9d"
            fillOpacity={0.6}
          />
          <image
  href="/radar-logo.png"
  x="50%"
  y="50%"
  width="120"
  height="120"
  style={{
    transform: "translate(-60px, -60px)"
  }}
/>
        </RadarChart>
      </ResponsiveContainer>
    )}

    {/* FLOATING POP-UP */}
   {selectedCategoryId && statementDetails.length > 0 && (
      <div
        style={{
          position: "absolute",
          top: "100px",
          right: "40px",
          width: "360px",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <strong style={{ fontSize: "16px" }}>
  {categoryMap.find((c) => c.id === selectedCategoryId)?.name[language] || selectedCategoryId}
</strong>

          <button
            onClick={() => setSelectedCategoryId(null)}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        <ul style={{ paddingLeft: "1rem" }}>
          {statementDetails.map((item, index) => (
  <li
    key={index}
    style={{
      marginBottom: "0.5rem",
      backgroundColor:
        item.statement === mostVariableStatement &&
        chartData.find(c => c.categoryId === selectedCategoryId)?.highlights.includes("highestStdDev")
          ? "#fef3e2" // subtle orange
          : undefined,
      padding: item.statement === mostVariableStatement ? "0.5rem" : undefined,
      borderRadius: "4px"
    }}
  >
    <em>{item.statement}</em>
    <br />
    Manager: {item.managerAvg} | Team: {item.teamAvg}
  </li>
))}

        </ul>
      </div>
    )}
  </div>
</div>
{/* AI-GENERATED REPORT */}
<div
  style={{
    maxWidth: "1000px",
    margin: "2rem auto 0",
    padding: "1.5rem",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e1e4e8",
    borderRadius: "8px",
    lineHeight: 1.6,
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "1rem",
      marginBottom: "1rem",
    }}
  >
    <h2
      style={{
        margin: 0,
        fontSize: "1.4rem",
      }}
    >
      {language === "en"
        ? "TeamScan analysis"
        : "Phân tích TeamScan"}
    </h2>

    <button
      onClick={() => setAiRefreshKey((current) => current + 1)}
      disabled={aiReportLoading}
      style={{
        padding: "0.4rem 0.8rem",
        fontSize: "0.9rem",
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: "4px",
        cursor: aiReportLoading ? "not-allowed" : "pointer",
        opacity: aiReportLoading ? 0.6 : 1,
      }}
    >
      {aiReportLoading
        ? language === "en"
          ? "Checking..."
          : "Đang kiểm tra..."
        : language === "en"
        ? "Refresh analysis"
        : "Làm mới phân tích"}
    </button>
  </div>

  {aiReportLoading && (
    <p style={{ color: "#666" }}>
      {language === "en"
        ? "Preparing the analysis. This may take a moment..."
        : "Đang chuẩn bị phần phân tích. Quá trình này có thể mất một chút thời gian..."}
    </p>
  )}

  {aiReportError && !aiReportLoading && (
    <div
      style={{
        padding: "0.75rem",
        backgroundColor: "#fff3f3",
        border: "1px solid #f0c2c2",
        borderRadius: "4px",
        color: "#9c2f2f",
      }}
    >
      {language === "en"
        ? "The analysis is temporarily unavailable. The other results are not affected."
        : "Phần phân tích hiện chưa khả dụng. Các kết quả khác không bị ảnh hưởng."}
    </div>
  )}

  {aiReport && !aiReportLoading && (
    <>
      <section style={{ marginBottom: "1.75rem" }}>
        <h3 style={{ marginBottom: "0.5rem" }}>
          {language === "en"
            ? "Interpretation"
            : "Diễn giải kết quả"}
        </h3>

        <div>
  {renderAIText(
    language === "en"
      ? aiReport.interpretationEn
      : aiReport.interpretationVn
  )}
</div>
      </section>

      <section>
        <h3 style={{ marginBottom: "0.5rem" }}>
          {language === "en"
            ? "What to do next"
            : "Các bước tiếp theo"}
        </h3>

        <div>
  {renderAIText(
    language === "en"
      ? aiReport.nextStepsEn
      : aiReport.nextStepsVn
  )}
</div>
      </section>

      <p
        style={{
          marginTop: "1.5rem",
          marginBottom: 0,
          fontSize: "0.8rem",
          color: "#777",
        }}
      >
        {language === "en"
          ? "This analysis is intended to support a team conversation. It should not be treated as a diagnosis or final conclusion."
          : "Phần phân tích này nhằm hỗ trợ cuộc trao đổi trong nhóm. Không nên xem đây là chẩn đoán hoặc kết luận cuối cùng."}
      </p>
    </>
  )}
</div>
      
</div>
  );
}
